/**
 * useCreateOffPageBrief — Panel A generate button.
 *
 * `POST /v1/briefs/:briefId/off-page-brief`. On success:
 *  - invalidates the list + eligible-briefs query keys so the table refreshes.
 *
 * The mutation surface preserves the raw `ApiError` (or any thrown value) so
 * the caller can pattern-match on `error.code`:
 *  - `SOURCE_BRIEF_NOT_COMPLETED` — the source brief isn't `completed` yet.
 *  - `OFF_PAGE_BRIEF_ALREADY_EXISTS` — an OPB exists; UI can offer "Open".
 *  - `QUOTA_EXCEEDED` — tenant is out of quota.
 *
 * Callers must never inspect the HTTP status; the API error `code` is the
 * stable contract per §5.6.1 + `coding-principles.md` §8.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateOffPageBriefRequest,
  CreateOffPageBriefResponse,
} from '@seo/types';
import { apiPost } from '@/lib/api';

export interface UseCreateOffPageBriefArgs {
  briefId: string;
  body?: CreateOffPageBriefRequest;
}

export function useCreateOffPageBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, body }: UseCreateOffPageBriefArgs) =>
      apiPost<CreateOffPageBriefResponse>(
        `/v1/briefs/${briefId}/off-page-brief`,
        body ?? {},
      ),
    onSuccess: () => {
      // Both queries feed the Briefs sub-tab; refresh so the row transitions
      // from "Generate" to "Open" without a full page reload.
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'list'],
      });
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'eligible-source-briefs'],
      });
    },
  });
}
